import React, { ComponentType, lazy, Suspense } from 'react';

import Loader from 'components/common/Loader';
import { getIsChunkLoadError } from 'utils/lazyWithRetry';

export const SuspenseWrapper: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  return <Suspense fallback={<Loader isVisible />}>{children}</Suspense>;
};

/**
 * Helper function to safely access sessionStorage
 * Returns null if sessionStorage is not available (e.g., in Node.js environment)
 */
const getSessionStorage = (key: string): string | null => {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(key) : null;
  } catch {
    return null;
  }
};

const setSessionStorage = (key: string, value: string): void => {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(key, value);
    }
  } catch {
    // Silently fail if sessionStorage is not available
  }
};

/**
 * @description
 *  https://medium.com/@alonmiz1234/retry-dynamic-imports-with-react-lazy-c7755a7d557a
 */

export const lazyWithRetries = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  componentImport: any
) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  lazy((): Promise<{ default: ComponentType<any> }> => {
    return new Promise((resolve, reject) => {
      // check if the window has already been refreshed
      const hasRefreshed = JSON.parse(getSessionStorage('retry-lazy-refreshed') || 'false');
      // try to import the component
      componentImport()
        .then(
          (
            component: // eslint-disable-next-line @typescript-eslint/no-explicit-any
              | { default: ComponentType<any> }
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              | PromiseLike<{ default: ComponentType<any> }>
          ) => {
            setSessionStorage('retry-lazy-refreshed', 'false'); // success so reset the refresh
            resolve(component);
          }
        )
        .catch((error: Error) => {
          // Check if this is a chunk loading error (Vite/Webpack dynamic import failure)
          // This happens during version updates when old chunks are no longer available
          const isChunkLoadError = getIsChunkLoadError(error);

          if (!hasRefreshed) {
            // not been refreshed yet
            setSessionStorage('retry-lazy-refreshed', 'true'); // we are now going to refresh

            if (isChunkLoadError) {
              setTimeout(() => {
                window.location.reload(); // dynamically reload the page
              }, 300);
              return;
            }
            return window.location.reload(); // refresh the page
          }
          reject(error); // Default error behaviour as already tried refresh
        });
    });
  });
